/**
 * Tethering, the parts that are not a component (CU-869fkr2yp, plan § 3.1).
 *
 * - LOR values are the brackets of the tenant's read pricelists, one
 *   option per reference LOR (the value stored is the reference, the unit
 *   the engine searches and writes: plan, answer 2). A reference cut two
 *   ways in two products shows both ranges: `8–9 / 9 · ref 9`.
 * - A value may head several tethers of its dimension; a child belongs to
 *   one tether and is never a parent in it (brief criterion 13). A value
 *   that cannot be picked is disabled with the tether that uses it, never
 *   hidden.
 * - Whether a tether applies to a strategy is the engine's rule, returned
 *   by the rate-shops estimate (`tethers[sid].fits`): this module only
 *   counts it and turns its reasons into sentences (criteria 9-10, 15).
 *
 * Pure module: no React, no fetch. Unit-tested with `node --test`.
 */
import { bracketLabel, bracketsFromProduct } from '../../Page/@services/durations.js'

const DIM_SHORT = { lor: 'LOR', category: 'CATEGORY', acriss: 'ACRISS', office: 'OFFICE' }
const DIM_NAME = { lor: 'Rental length', office: 'Office', acriss: 'ACRISS', category: 'Category' }

/** `Rental length`, `Office`, `ACRISS`, `Category` — Activity names the dimension, not its code. */
export function dimensionLabel(dim) {
	return DIM_NAME[dim] || dim
}

const rangeText = (b) => (b.open ? `${b.durationFrom}+` : b.durationFrom === b.durationTo ? `${b.durationFrom}` : `${b.durationFrom}–${b.durationTo}`)

/**
 * `tenantBrackets(products, pricelists)` → `[{ code, referenceLor, ranges, label }]`, one per
 * reference LOR across every product, sorted. `code` is the reference as text (what a tether
 * stores). One range → the wizard's `bracketLabel`; several → `8–9 / 9 · ref 9`. References are
 * not clamped to the wizard's 180-day field.
 */
export function tenantBrackets(products, pricelists) {
	const byRef = new Map()
	for (const product of products || []) {
		for (const b of bracketsFromProduct(product, pricelists, { max: Infinity })) {
			const list = byRef.get(b.referenceLor) || []
			if (!list.some(x => x.durationFrom === b.durationFrom && x.open === b.open && x.durationTo === b.durationTo)) list.push(b)
			byRef.set(b.referenceLor, list)
		}
	}
	return [...byRef.entries()].sort((a, b) => a[0] - b[0]).map(([ref, ranges]) => {
		ranges.sort((a, b) => a.durationFrom - b.durationFrom || (a.open ? 1 : 0) - (b.open ? 1 : 0) || a.durationTo - b.durationTo)
		const label = ranges.length === 1 ? bracketLabel(ranges[0]) : `${ranges.map(rangeText).join(' / ')} · ref ${ref}`
		return { code: String(ref), referenceLor: ref, ranges, label }
	})
}

/**
 * The one label of a tether value (criterion 22): LOR by its bracket (a value that is no
 * reference any more, the bare number), category by its name, office and ACRISS by their code.
 * `ctx = { brackets, categories }`.
 */
export function valueLabel(dimension, value, ctx = {}) {
	const code = value == null ? '' : String(value)
	if (dimension === 'lor') return (ctx.brackets || []).find(b => b.code === code)?.label || code
	if (dimension === 'category') return (ctx.categories || []).find(c => c.code === code)?.name || code
	return code
}

/** `LOR: 7 → 4`, `ACRISS: CDMR → CDAR, CFMR`, `OFFICE: A1 → 4 children` with the labels of `label(dim, value)`. */
export function autoName(t, label = (_, v) => String(v)) {
	const dim = DIM_SHORT[t.dimension] || t.dimension
	const p = label(t.dimension, t.parent)
	const children = t.children || []
	if (children.length === 0) return `${dim}: ${p} → …`
	if (children.length <= 3) return `${dim}: ${p} → ${children.map(c => label(t.dimension, c)).join(', ')}`
	return `${dim}: ${p} → ${children.length} children`
}

const nameOf = (t) => t.name || autoName(t)

/**
 * The options of the parent or children picker, every value kept: `[{ code, label, disabled,
 * reason, note, stale? }]`. As parent, a child of another tether is disabled ("in use by …") and
 * a parent of another is enabled with "also parent of …"; as child, any value used elsewhere is
 * disabled. The edited tether never blocks itself, and its own values that are not in `values`
 * (a LOR that is no bracket any more) come first, flagged `stale`.
 */
export function pickerOptions(tethers, dimension, role, editingId, values) {
	const parentsOf = new Map(), childOf = new Map()
	for (const t of tethers || []) {
		if (t.dimension !== dimension || (editingId != null && t.id === editingId)) continue
		if (t.parent != null && t.parent !== '') {
			const p = String(t.parent)
			parentsOf.set(p, [...(parentsOf.get(p) || []), nameOf(t)])
		}
		for (const c of t.children || []) if (!childOf.has(String(c))) childOf.set(String(c), nameOf(t))
	}
	const judge = (o) => {
		const code = String(o.code)
		const blocker = role === 'parent' ? childOf.get(code) : (childOf.get(code) || parentsOf.get(code)?.[0])
		const also = role === 'parent' && !blocker && parentsOf.has(code) ? `also parent of ${parentsOf.get(code).join(', ')}` : null
		return { ...o, code, disabled: !!blocker, reason: blocker ? `in use by ${blocker}` : null, note: also }
	}
	const known = new Set((values || []).map(v => String(v.code)))
	const editing = editingId == null ? null : (tethers || []).find(t => t.id === editingId)
	const own = !editing || editing.dimension !== dimension ? []
		: (role === 'parent' ? [editing.parent] : editing.children || []).filter(v => v != null && v !== '').map(String)
	const stale = [...new Set(own.filter(v => !known.has(v)))]
		.map(code => ({ ...judge({ code, label: code }), stale: true }))
	return [...stale, ...(values || []).map(judge)]
}

/** The LOR values of a tether that are no reference of any read pricelist (criterion 14). */
export function staleValues(tether, brackets) {
	if (!tether || tether.dimension !== 'lor') return []
	const known = new Set((brackets || []).map(b => String(b.code)))
	return [tether.parent, ...(tether.children || [])]
		.filter(v => v != null && v !== '').map(String).filter(v => !known.has(v))
}

/**
 * A strategy's status as the strategies table writes it (`enabled` |
 * `disabled` | `draft`, the engine's value): `{ label, on }`.
 */
export function strategyStatus(status) {
	if (status === 'enabled') return { label: 'Enabled', on: true }
	if (status === 'draft') return { label: 'Draft', on: false }
	if (status === 'disabled') return { label: 'Disabled', on: false }
	return { label: status ? String(status) : '—', on: false }
}

const plural = (n) => `${n} strateg${n === 1 ? 'y' : 'ies'}`

/**
 * Where a tether applies, from the estimate's `tethers` (criterion 15): `{ count, label,
 * applies: [{id, name, status}], notApplying: [{strategy, fit}] }`. Only strategies that chose
 * it count; one that chose it where it does not apply is listed apart, with the engine's fit.
 * Category → "not applied yet". No data → null.
 */
export function appliesSummary(tetherId, dimension, fitsByStrategy) {
	if (dimension === 'category') return { count: 0, label: 'not applied yet', applies: [], notApplying: [] }
	if (!fitsByStrategy) return null
	const applies = [], notApplying = []
	for (const [sid, s] of Object.entries(fitsByStrategy)) {
		const fit = (s?.fits || []).find(f => String(f.tether_id) === String(tetherId))
		if (!fit || !fit.chosen) continue
		const strategy = { id: String(sid), name: s.name, status: s.status }
		if (fit.applies) applies.push(strategy)
		else notApplying.push({ strategy, fit })
	}
	const count = applies.length
	const label = count ? plural(count) : notApplying.length ? `applies to none · picked in ${notApplying.length}` : 'applies to none'
	return { count, label, applies, notApplying }
}

const NO_PRODUCT = 'Pick a product in Basic, card 2, to check LOR tethers'

function reasonText(reason, v, product) {
	switch (reason) {
		case 'not_a_bracket':          return `${v} is not a bracket of ${product}'s read pricelists`
		case 'bracket_on_some_rows':   return `${v} is a bracket only on some of ${product}'s pricelists`
		case 'outside_rental_lengths': return `${v} is outside this strategy's Rental lengths`
		case 'outside_offices':        return `Office ${v} is outside this strategy's offices`
		case 'outside_cars':           return `${v} is outside this strategy's cars`
		default:                       return reason ? `${v}: ${reason}` : null
	}
}

// The tether-level reasons (`Fit.reason`) when no value says more.
const TOP_REASON = { no_child_fits: 'no child fits this strategy', partly: 'some children do not fit this strategy' }

/**
 * The wizard's sentence for the engine's fit of one tether (criterion 10), or null when it
 * applies in full: "Doesn't apply here: …" or "Partly: …", naming each value that does not fit
 * and why. `labels = { productName, value(v) }`.
 */
export function fitSentence(fit, labels = {}) {
	if (!fit) return null
	if (fit.reason === 'not_applied_yet') return 'Not applied by the engine yet'
	const values = [fit.parent, ...(fit.children || [])].filter(Boolean)
	if (fit.reason === 'no_product' || values.some(v => v.reason === 'no_product')) return NO_PRODUCT
	const product = labels.productName || 'the product'
	const label = labels.value || String
	const parts = values
		.filter(v => !v.fits || v.partly)
		.map(v => reasonText(v.reason, label(v.value), product))
		.filter(Boolean)
	if (fit.applies) {
		if (parts.length) return `Partly: ${parts.join(', and ')}`
		return fit.reason === 'partly' ? `Partly: ${TOP_REASON.partly}` : null
	}
	if (!parts.length) return fit.reason ? `Doesn't apply here: ${TOP_REASON[fit.reason] || fit.reason}` : "Doesn't apply here"
	return `Doesn't apply here: ${parts.join(', and ')}`
}

/** The delete confirmation (criterion 20). `count` null = the count could not be read. */
export function deleteSentence(tether, count) {
	const head = `Delete ${nameOf(tether)}?`
	const tail = 'Prices already sent stay as they are.'
	if (count == null) return `${head} The strategies that use it stop using it on the next pass. ${tail}`
	if (count === 0) return `${head} It applies to no strategy. ${tail}`
	return `${head} It applies to ${plural(count)}; ${count === 1 ? 'it stops' : 'they stop'} using it on the next pass. ${tail}`
}

const snapshotBracket = (b) => {
	const from = Number(b?.duration_from), ref = Number(b?.reference_lor)
	if (!Number.isInteger(from) || !Number.isInteger(ref)) return null
	const open = b.duration_to == null
	return { durationFrom: from, durationTo: open ? null : Number(b.duration_to), referenceLor: ref, open }
}

/**
 * One side of an Activity tether (`via_tether` or a parent's note): for a rental length, the
 * bracket actually written on that row (`parent_bracket` / `child_bracket`, U1); without it, or
 * for another dimension, the value.
 */
export function tetherSideLabel(vt, side) {
	const value = vt?.[`${side}_value`]
	if (vt?.dimension === 'lor') {
		const b = snapshotBracket(vt?.[`${side}_bracket`])
		if (b) return bracketLabel(b)
	}
	return value == null ? '—' : String(value)
}

/** `Rental length 7 → 8–9 · ref 9`, `Office A → B`, `ACRISS CDMR → CDAR` (criterion 22). */
export function tetherPairLabel(vt) {
	return `${dimensionLabel(vt?.dimension)} ${tetherSideLabel(vt, 'parent')} → ${tetherSideLabel(vt, 'child')}`
}
